import React, { useMemo, useState } from 'react';
import { Video, Phone, MessageSquare, Star, List, Calendar as CalendarIcon, Mail } from 'lucide-react';
import { mockClasses, Format, CECategory, ClassRecord } from '../data/classes';
import { FindAClassFilters } from '../components/classes/FindAClassFilters';
import { ClassCalendar } from '../components/classes/ClassCalendar';
import { ClassList } from '../components/classes/ClassList';
import { ClassDetailDrawer } from '../components/classes/ClassDetailDrawer';
import { DayClassesDrawer } from '../components/classes/DayClassesDrawer';
import { SalespersonRequirementTable, BrokerRequirementTable } from '../components/RequirementTables';
export function LiveClasses() {
  // Filter State
  const [formatFilter, setFormatFilter] = useState<Format | 'All Formats'>('All Formats');
  const [categoryFilter, setCategoryFilter] = useState<CECategory | 'All Categories'>('All Categories');
  const [searchQuery, setSearchQuery] = useState('');
  // View State
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 5, 1)); // June 2026
  // Drawer State
  const [selectedClass, setSelectedClass] = useState<ClassRecord | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState<string | null>(null);
  const [selectedDayClasses, setSelectedDayClasses] = useState<ClassRecord[]>([]);
  const [isDayDrawerOpen, setIsDayDrawerOpen] = useState(false);
  // Filter Logic
  const filteredClasses = useMemo(() => {
    return mockClasses.filter(cls => {
      // Hide past classes (assuming today is June 5, 2026)
      const classDate = new Date(cls.date + 'T00:00:00');
      const today = new Date('2026-06-05T00:00:00');
      if (classDate < today) return false;
      // Format filter
      if (formatFilter !== 'All Formats' && cls.format !== formatFilter) return false;
      // Category filter
      if (categoryFilter !== 'All Categories' && cls.ceCategory !== categoryFilter) return false;
      // Search query
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        const matchesTitle = cls.title.toLowerCase().includes(query);
        const matchesInstructor = cls.instructorName.toLowerCase().includes(query);
        const matchesLocation = cls.location.toLowerCase().includes(query);
        const matchesDesc = cls.description.toLowerCase().includes(query);
        if (!matchesTitle && !matchesInstructor && !matchesLocation && !matchesDesc) return false;
      }
      return true;
    }).sort((a, b) => {
      // Sort by date soonest first
      const dateA = new Date(a.date + 'T' + (a.startTime.includes('AM') ? a.startTime.replace(' AM', ':00') : a.startTime.replace(' PM', ':00')));
      const dateB = new Date(b.date + 'T' + (b.startTime.includes('AM') ? b.startTime.replace(' AM', ':00') : b.startTime.replace(' PM', ':00')));
      return dateA.getTime() - dateB.getTime();
    });
  }, [formatFilter, categoryFilter, searchQuery]);
  const handleResetFilters = () => {
    setFormatFilter('All Formats');
    setCategoryFilter('All Categories');
    setSearchQuery('');
  };
  const handleShowNext = () => {
    // Clear filters so the soonest upcoming classes surface, switch to list, and scroll to results
    setFormatFilter('All Formats');
    setCategoryFilter('All Categories');
    setSearchQuery('');
    setViewMode('list');
    const results = document.getElementById('class-results');
    if (results) {
      results.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handleClassClick = (classRecord: ClassRecord) => {
    setSelectedClass(classRecord);
    setIsDetailDrawerOpen(true);
  };
  const handleDayClick = (date: string, dayClasses: ClassRecord[]) => {
    setSelectedDayDate(date);
    setSelectedDayClasses(dayClasses);
    setIsDayDrawerOpen(true);
  };
  return <div className="min-h-screen bg-white pt-40 lg:pt-52 pb-20" data-id="element-1185">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1186">
        {/* Page Header */}
        <div className="mb-12 text-center" data-id="element-1187">
          <div className="flex items-center justify-center mb-4" data-id="element-1188">
            <h1 className="text-4xl font-bold text-gray-900" data-id="element-1189">
              In-Person & Zoom Arizona Real Estate CE Classes
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1190">
            Find upcoming ADRE-approved Arizona real estate continuing education
            classes by date, class format, or CE category.
          </p>
        </div>

        {/* Find A Class Section */}
        <div id="find-a-class" className="scroll-mt-40" data-id="element-1191">
          <FindAClassFilters formatFilter={formatFilter} setFormatFilter={setFormatFilter} categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onReset={handleResetFilters} onShowNext={handleShowNext} resultCount={filteredClasses.length} data-id="element-1192" />

          {/* Free Classes Notice (near results) */}
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 px-4 py-3 rounded-md" data-id="element-1193">
            <p className="text-base font-semibold text-green-800 text-center" data-id="element-1194">
              Classes are FREE unless indicated otherwise
            </p>
            <p className="mt-2 text-sm text-green-800 text-center break-words" data-id="element-1195">
              Phoenix Realtors-sponsored classes display as: “FREE to Phoenix
              Realtors Members, Non-members $20 (thru Phoenix Portal).”
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex justify-end mb-6" data-id="element-1196">
            <div className="inline-flex bg-gray-100 rounded-lg p-1" data-id="element-1197">
              <button onClick={() => setViewMode('list')} className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-brand-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1198">
                <List className="w-4 h-4 mr-2" data-id="element-1199" />
                Upcoming Classes
              </button>
              <button onClick={() => setViewMode('calendar')} className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'calendar' ? 'bg-white text-brand-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1200">
                <CalendarIcon className="w-4 h-4 mr-2" data-id="element-1201" />
                Monthly Calendar
              </button>
            </div>
          </div>

          {/* Main Content Area */}
          <div id="class-results" className="mb-16 scroll-mt-40" data-id="element-1202">
            {viewMode === 'calendar' ? <div className="hidden md:block" data-id="element-1203">
                <ClassCalendar classes={filteredClasses} currentMonth={currentMonth} onMonthChange={setCurrentMonth} onClassClick={handleClassClick} onDayClick={handleDayClick} data-id="element-1204" />
              </div> : null}

            {/* Always show list on mobile if calendar is selected, or if list is selected */}
            <div className={viewMode === 'calendar' ? 'md:hidden' : 'block'} data-id="element-1205">
              <ClassList classes={filteredClasses} onClassClick={handleClassClick} data-id="element-1206" />
            </div>
          </div>
        </div>

        {/* CE Requirements Section (Moved from top) */}
        <div className="mb-16 border-t border-gray-200 pt-16" data-id="element-1207">
          <div className="text-center mb-10" data-id="element-1208">
            <h2 className="text-3xl font-bold text-gray-900" data-id="element-1209">
              CE Requirements
            </h2>
            <p className="text-gray-600 mt-2" data-id="element-1210">
              Review your ADRE renewal requirements below.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start" data-id="element-1211">
            <SalespersonRequirementTable data-id="element-1212" />
            <BrokerRequirementTable data-id="element-1213" />
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mt-4" data-id="element-1214">
            <p className="text-base text-gray-800 font-medium" data-id="element-1215">
              For your remaining 3 hours you may choose any credit type,
              providing you do not duplicate any course title that you have
              already taken within the renewal period. Different class name =
              Different course number.
            </p>
          </div>
        </div>

        {/* Questions Section */}
        <div className="mb-12 bg-brand-purple-50 rounded-lg p-6 border-2 border-brand-purple-200 text-center" data-id="element-1216">
          <h2 className="text-2xl font-bold text-brand-purple-900 mb-4" data-id="element-1217">
            Questions?
          </h2>
          <div className="flex items-center justify-center text-lg" data-id="element-1218">
            <Phone className="w-5 h-5 text-brand-purple-600 mr-2" data-id="element-1219" />
            <span className="text-gray-700" data-id="element-1220">Call or text to</span>
            <a href="tel:602-377-6207" className="ml-2 text-brand-purple-600 hover:text-brand-purple-700 font-bold" data-id="element-1221">
              602-377-6207
            </a>
          </div>
        </div>

        {/* Testimonial */}
        <div className="mb-12 bg-white rounded-lg border-2 border-gray-200 p-8 shadow-sm" data-id="element-1222">
          <div className="flex items-start mb-4" data-id="element-1223">
            <MessageSquare className="w-8 h-8 text-brand-purple-600 mr-4 flex-shrink-0 mt-1" data-id="element-1224" />
            <div data-id="element-1225">
              <div className="flex items-center mb-3" data-id="element-1226">
                <h3 className="text-lg font-bold text-gray-900 mr-2" data-id="element-1227">
                  Testimonial from Kresta Davis, REALTOR®
                </h3>
                <div className="flex" data-id="element-1228">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" data-id="element-1229" />)}
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed italic" data-id="element-1230">
                "Jon, once again, one of the BEST classes! I can tell you are
                spending time making Zoom work for you because it genuinely was
                one of the best zoom classes I have taken. Most are difficult to
                even stay awake, even with the polls! In person AND on Zoom,
                your classes are THE BEST!"
              </p>
            </div>
          </div>
        </div>

        {/* Combined Questions / Contact CTA */}
        <div className="mt-12 bg-brand-purple-50 rounded-lg p-8 text-center border border-brand-purple-100" data-id="element-1231">
          <h2 className="text-2xl font-bold text-brand-purple-900 mb-4" data-id="element-1232">
            QUESTIONS?
          </h2>
          <p className="text-gray-700 mb-6" data-id="element-1233">
            Have questions about upcoming classes or need help registering?
            We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1234">
            <a href="tel:602-377-6207" className="inline-flex items-center justify-center px-8 py-3 bg-brand-purple-600 text-white font-bold rounded-md hover:bg-brand-purple-700 transition-colors shadow-md" data-id="element-1235">
              <Phone className="w-5 h-5 mr-2" data-id="element-1236" />
              Call or Text / 602-377-6207
            </a>
            <a href="/Contact" className="inline-flex items-center justify-center px-8 py-3 bg-white text-brand-purple-600 font-bold rounded-md border-2 border-brand-purple-600 hover:bg-brand-purple-50 transition-colors shadow-sm" data-id="element-1237">
              <Mail className="w-5 h-5 mr-2" data-id="element-1238" />
              Contact Us / Email or Chat
            </a>
          </div>
        </div>
      </div>

      {/* Drawers */}
      <ClassDetailDrawer classRecord={selectedClass} isOpen={isDetailDrawerOpen} onClose={() => setIsDetailDrawerOpen(false)} data-id="element-1239" />

      <DayClassesDrawer date={selectedDayDate} classes={selectedDayClasses} isOpen={isDayDrawerOpen} onClose={() => setIsDayDrawerOpen(false)} onClassClick={handleClassClick} data-id="element-1240" />
    </div>;
}